import { CropRegion } from '@sparemin/api-sdk';
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { videoDimensionsSelector } from 'redux/modules/embed/selectors';
import useEditSceneModal from '../useEditSceneModal';
import { getSlotScaleInfo, translateCrop } from '../utils';
import { useEditSceneModalStateContext } from './EditSceneModalStateContext';
import useIsInteractionDisabled from './useIsInteractionDisabled';
import useSlots from './useSlots';

type UseSlotDragProps = {
  slotIndex?: number;
};

type DragState = {
  startPoint: { x: number; y: number };
  startCrop: CropRegion;
};

export default function useSlotDrag(props?: UseSlotDragProps) {
  const { slotIndex } = props ?? {};

  const { videoId } = useEditSceneModal();

  const { slots } = useSlots();

  const {
    dispatch,
    state: {
      controlPanelMode,
      draftCrop,
      previewDimensions,
      selectedSlotIndex,
    },
  } = useEditSceneModalStateContext();

  const { isInteractionDisabled } = useIsInteractionDisabled();

  const videoDimensions = useSelector(videoDimensionsSelector(videoId));
  const targetSlotIndex = slotIndex ?? selectedSlotIndex;
  const slot = slots?.[targetSlotIndex];

  const scaleInfo = getSlotScaleInfo({
    slot,
    crop: draftCrop,
    previewDimensions,
    videoDimensions,
  });

  const dragStateRef = useRef<DragState | null>(null);

  const isDraggable =
    !isInteractionDisabled &&
    controlPanelMode === 'adjust-crop' &&
    draftCrop &&
    slot &&
    scaleInfo &&
    videoDimensions;

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!isDraggable) return;

      event.preventDefault();
      event.stopPropagation();

      dragStateRef.current = {
        startPoint: { x: event.clientX, y: event.clientY },
        startCrop: draftCrop,
      };

      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [draftCrop, isDraggable],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!dragStateRef.current || !scaleInfo || !videoDimensions) {
        return;
      }

      const { startPoint, startCrop } = dragStateRef.current;
      const dxPixels = event.clientX - startPoint.x;
      const dYPixels = event.clientY - startPoint.y;

      const { slotRect } = scaleInfo;
      const cropWidth = startCrop.right - startCrop.left;
      const cropHeight = startCrop.bottom - startCrop.top;

      const scaleX = cropWidth / slotRect.width;
      const scaleY = cropHeight / slotRect.height;

      const nextCrop = translateCrop(
        startCrop,
        { x: -dxPixels * scaleX, y: -dYPixels * scaleY },
        videoDimensions,
      );

      dispatch({
        type: 'adjust-crop-update',
        payload: { crop: nextCrop },
      });
    },
    [dispatch, scaleInfo, videoDimensions],
  );

  const clearDragState = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!dragStateRef.current) {
        return;
      }

      clearDragState();
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    [clearDragState],
  );

  const handlePointerCancel = useCallback(() => {
    clearDragState();
  }, [clearDragState]);

  return {
    isDraggable,
    draggableProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
    },
  };
}
